import React, { useState } from 'react';  
import { Link, useNavigate } from 'react-router-dom';  
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';  
import { Input } from '../../components/ui/input';  
import { Label } from '../../components/ui/label';  
import { Button } from '../../components/ui/button';  
import { useToast } from '../../hooks/use-toast';  
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';  
import { getAuth, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";  

export const ChangePassword = () => {  
    const [currentPassword, setCurrentPassword] = useState('');  
    const [newPassword, setNewPassword] = useState('');  
    const [confirmPassword, setConfirmPassword] = useState('');  
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);  
    const [showNewPassword, setShowNewPassword] = useState(false);  
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);  
    const [isSubmitting, setIsSubmitting] = useState(false);  
    const { toast } = useToast();  
    const auth = getAuth();  
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {  
        e.preventDefault();  

        // Simple validation  
        if (!currentPassword || !newPassword || !confirmPassword) {  
            toast({  
                title: "Error",  
                description: "Please fill all fields",  
                variant: "destructive",  
            });  
            return;  
        }  

        if (newPassword !== confirmPassword) {  
            toast({  
                title: "Error",  
                description: "New passwords do not match",  
                variant: "destructive",  
            });  
            return;  
        }  

        if (newPassword.length < 8) {  
            toast({  
                title: "Error",  
                description: "Password must be at least 8 characters long",  
                variant: "destructive",  
            });  
            return;  
        }  

        setIsSubmitting(true);  

        try {  
            // Re-authenticate user  
            const user = auth.currentUser;  
            if (user) {  
                const credential = EmailAuthProvider.credential(user.email, currentPassword);  
                await reauthenticateWithCredential(user, credential);  

                // Update password  
                await updatePassword(user, newPassword);  
                toast({  
                    title: "Password Updated",  
                    description: "Your password has been changed successfully.",  
                });  

                // Reset form  
                setCurrentPassword('');  
                setNewPassword('');  
                setConfirmPassword('');  
                navigate('/dashboard');
            }  
        } catch (error) {  
            console.error("Error updating password:", error);  
            toast({  
                title: "Update Failed",  
                description: "Could not update your password. Please check your current password.",  
                variant: "destructive",  
            });  
        } finally {  
            setIsSubmitting(false);  
        }  
    };  

    return (  
        <div className="max-w-md mx-auto animate-fade-in">  
            <div className="mb-6">  
                <Link  
                    to="/profile"  
                    className="inline-flex items-center gap-2 text-bytesplash-gray-600 hover:text-bytesplash-primary transition-colors"  
                >  
                    <ArrowLeft size={18} />  
                    <span>Back to Profile</span>  
                </Link>  
            </div>  

            <Card className="shadow-soft">  
                <CardHeader>  
                    <CardTitle>Change Password</CardTitle>  
                    <CardDescription>  
                        Update your password to keep your account secure  
                    </CardDescription>  
                </CardHeader>  
                <CardContent>  
                    <form onSubmit={handleSubmit} className="space-y-4">  
                        <div className="space-y-2">  
                            <Label htmlFor="current-password">Current Password</Label>  
                            <div className="relative">  
                                <Input  
                                    id="current-password"  
                                    type={showCurrentPassword ? "text" : "password"}  
                                    placeholder="••••••••"  
                                    value={currentPassword}  
                                    onChange={(e) => setCurrentPassword(e.target.value)}  
                                    required  
                                    className="form-input pr-10"  
                                />  
                                <button  
                                    type="button"  
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"  
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}  
                                >  
                                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}  
                                </button>  
                            </div>  
                        </div>  

                        <div className="space-y-2">  
                            <Label htmlFor="new-password">New Password</Label>  
                            <div className="relative">  
                                <Input  
                                    id="new-password"  
                                    type={showNewPassword ? "text" : "password"}  
                                    placeholder="••••••••"  
                                    value={newPassword}  
                                    onChange={(e) => setNewPassword(e.target.value)}  
                                    required  
                                    className="form-input pr-10"  
                                />  
                                <button  
                                    type="button"  
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"  
                                    onClick={() => setShowNewPassword(!showNewPassword)}  
                                >  
                                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}  
                                </button>  
                            </div>  
                            <p className="text-xs text-bytesplash-gray-500">  
                                Password must be at least 8 characters long  
                            </p>  
                        </div>  

                        <div className="space-y-2">  
                            <Label htmlFor="confirm-password">Confirm New Password</Label>  
                            <div className="relative">  
                                <Input  
                                    id="confirm-password"  
                                    type={showConfirmPassword ? "text" : "password"}  
                                    placeholder="••••••••"  
                                    value={confirmPassword}  
                                    onChange={(e) => setConfirmPassword(e.target.value)}  
                                    required  
                                    className="form-input pr-10"  
                                />  
                                <button  
                                    type="button"  
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"  
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}  
                                >  
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}  
                                </button>  
                            </div>  
                        </div>  

                        <Button   
                          type="submit"   
                          className="w-full bg-bytesplash-primary hover:bg-bytesplash-dark"  
                          disabled={isSubmitting}  
                        >  
                          {isSubmitting ? (  
                            <span className="flex items-center gap-2">  
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">  
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>  
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>  
                              </svg>  
                              Updating...  
                            </span>  
                          ) : (  
                            'Update Password'  
                          )}  
                        </Button>  
                    </form>  
                </CardContent>  
                <CardFooter className="flex justify-center border-t pt-4">  
                    <Link   
                      to="/forgot-password"   
                      className="text-bytesplash-primary hover:underline text-sm"  
                    >  
                      Forgot your password?  
                    </Link>  
                </CardFooter>  
            </Card>  
        </div>  
      
  );  
};  